/**
 * SOLID Review
 * S — Strong. One use case: start the game — the plan, the server's first round, the table at its first roll.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandGameStarted, ICommandNothing, IPayloadStartGame } from '../../domain/ports/schemas/TableMoves';
import type { IPayloadServerStartRound } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/** The host starts the game (FE1's startGame): the server opens the round, rolled first by the host; the table follows. */
export class StartGameUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTrip<IPropsClientTableInput, ICommandNothing, IPayloadStartGame, TAnswered<ICommandGameStarted['answer']>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planStartGame(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadStartGame) {
    const opening: IPayloadServerStartRound = { roomId: plan.roomId, rollerId: plan.playerId };
    return this.deps.rounds.startRound(ctx, opening);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadStartGame, answered: TAnswered<ICommandGameStarted['answer']>) {
    const started: ICommandGameStarted = { request: plan, answer: answered.value };
    return this.deps.decisions.gameStarted(ctx, input, started);
  }
}
